import { useLocation, useNavigate } from "react-router-dom";
import { useGetConsentList, useGetFitlerList } from "../api/ConsentApi";
import { useEffect, useMemo, useState, useCallback } from "react";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import PageHeader from "../shared-components/PageHeader";
import Pagination from "../shared-components/Pagination";
import Table from "./Table";
import { Modal } from "../shared-components/Modal";
import { ConsentListInputApi } from "../api/Consent";
import { ListViewControl, useControlGroupContext } from "../api/ControlGroup";
import FilterComponent from "../shared-components/Filter";
import { KeyValue } from "../reports/utils";
import { capitalizeFirstLetter } from "../utils";

const Consent = () => {
  const {
    isLoading: loadingConsent,
    data: consentData,
    mutate: getConsentList,
    error: errorGettingConsent,
  } = useGetConsentList();
  const {
    isLoading: loadingFilter,
    data: filterData,
    mutate: getFilterData,
  } = useGetFitlerList();

  const pageSize = 25;
  const [total, setTotal] = useState(0);
  const [openModal, setOpenModal] = useState<string | null>(null);
  const [backup, setBackup] = useState<ListViewControl[] | null>(null);
  const [orderBy, setOrderBy] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);

  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const { controlGroups, setControlGroups, getControlByName } =
    useControlGroupContext();

  function fetchConsentListBaseProps() {
    const fetchAttr: ConsentListInputApi = {
      filters: controlGroups,
      size: pageSize,
      page: page,
      order_by: Array.from(orderBy),
    };
    getConsentList(fetchAttr);
    getFilterData(controlGroups);
  }

  const fetchConsentList = useCallback(() => {
    fetchConsentListBaseProps();
    // eslint-disable-next-line
  }, [getConsentList, getFilterData, orderBy, page]);

  useEffect(() => {
    fetchConsentList();
  }, [fetchConsentList]);

  useEffect(() => {
    setTotal(consentData?.total_rows ?? 1);
  }, [consentData]);

  useEffect(() => {
    if (searchParams.get("page")) {
      setPage(Number(searchParams.get("page")));
    }
  }, [searchParams]);

  const openModalByName = (name: string) => {
    setBackup(structuredClone(controlGroups));
    setOpenModal(name);
  };

  const handleOrderByChange = (name: string, action: "add" | "remove") => {
    setOrderBy((prevOrderBy) => {
      const newSet = new Set(prevOrderBy);
      action === "add" ? newSet.add(name) : newSet.delete(name);
      return newSet;
    });
  };

  const hasFilters = (name: string): boolean => {
    const control = controlGroups.find((control) => control.name === name);
    return control
      ? control.controls.filters?.length > 0 ||
          control.controls.direction?.trim() !== ""
      : false;
  };

  const renameCompany = (name: string) =>
    name === "companies" ? "company_names" : name;

  const handleClose = () => {
    if (backup) {
      setControlGroups(backup);
    }
    setOpenModal(null);
  };

  const handleSaveFilter = (page: number, controlSaved?: string) => {
    if (controlSaved) {
      if (getControlByName(controlSaved)?.controls.direction) {
        handleOrderByChange(controlSaved, "add");
      }
    }
    searchParams.set("page", "1");
    navigate({ search: searchParams.toString() });
    fetchConsentListBaseProps();
    setOpenModal(null);
  };

  if (errorGettingConsent) {
    return <ErrorMessageContainer message="Couldn't load consent list" />;
  }
  function consentMapper(
    list: string[],
    groupName: string
  ): (string | KeyValue)[] {
    if (groupName === "status") {
      return list.map((item) => {
        return {
          key: item,
          value: item === "ready" ? "Completed" : capitalizeFirstLetter(item),
        };
      });
    }
    return list;
  }

  const tableHeader = [
    { name: "Name", sortController: "names" },
    { name: "Company", sortController: "companies" },
    { name: "Status", sortController: "status" },
    { name: "Date Sent", sortController: "created_at" },
    { name: "Date Completed", sortController: "signed_at" },
    { name: "View Form" },
    { name: "Report ID" },
  ];

  return (
    <>
      <PageHeader title={"Consent Form Status"} />
      <div className="w-5/12"></div>
      <div>
        <Table
          isLoading={loadingConsent}
          header={tableHeader}
          openFilter={openModalByName}
          hasFilters={hasFilters}
          data={consentData?.items ?? []}
          onEventComplete={fetchConsentList}
        />
      </div>
      <Pagination
        pageSize={pageSize}
        currentPage={page}
        totalCount={total}
        search={""}
      />

      {filterData &&
        controlGroups.map((group, id) => (
          <Modal
            key={id}
            open={openModal === group.name}
            width="w-96"
            onClose={handleClose}
          >
            <FilterComponent
              controlGroup={group}
              date={false}
              parentList={consentMapper(
                filterData[renameCompany(group.name)],
                group.name
              )}
              loading={loadingFilter}
              saveFilter={handleSaveFilter}
              cleared={() => {
                handleOrderByChange(group.name, "remove");
                handleSaveFilter(1, group.name);
              }}
              canceled={handleClose}
            />
          </Modal>
        ))}
    </>
  );
};

export default Consent;
